'use client';

import Link from 'next/link';
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { cn } from 'src/utils/utils';

import { Button } from 'src/components/shadcn/ui/button';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from 'src/components/shadcn/ui/field';
import { Input } from 'src/components/shadcn/ui/input';
import { SocialAuthButton } from 'src/components/atoms/Buttons/SocialAuthButton';
import { LoginCredentials } from 'src/store';
import { emailPattern } from 'src/utils';

interface ModernLoginFormProps {
  onLogin: (data: LoginCredentials) => void;
  formError?: string | null;
  isLoading?: boolean;
  className?: string;
}

export const ModernLoginForm: React.FC<ModernLoginFormProps> = ({
  onLogin,
  formError,
  isLoading = false,
  className,
  ...props
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginCredentials>({
    mode: 'onSubmit',
  });

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      onSubmit={handleSubmit(onLogin)}
      {...props}
    >
      <FieldGroup>
        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-2xl font-bold">Login to your account</h1>
          <p className="text-muted-foreground text-sm text-balance">
            Enter your email below to login to your account
          </p>
        </div>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Controller
            name="email"
            control={control}
            rules={{
              pattern: emailPattern,
              required: 'Email is required',
            }}
            render={({ field }) => (
              <Input
                {...field}
                id="email"
                type="email"
                placeholder="m@example.com"
                className={errors.email ? 'border-red-500' : ''}
                disabled={isLoading}
              />
            )}
          />
          {errors.email && (
            <p className="text-sm text-red-500">
              {errors.email.message || 'Email is not in correct format'}
            </p>
          )}
        </Field>
        <Field>
          <div className="flex items-center">
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Link
              href="/auth/forgot-password"
              className="ml-auto text-sm underline-offset-4 hover:underline"
            >
              Forgot your password?
            </Link>
          </div>
          <Controller
            name="password"
            control={control}
            rules={{ required: 'Password is required' }}
            render={({ field }) => (
              <div className="relative">
                <Input
                  {...field}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                  placeholder="********"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            )}
          />
          {errors.password && (
            <p className="text-sm text-red-500">{errors.password.message}</p>
          )}
        </Field>
        {formError && (
          <p className="text-red-500 font-semibold text-sm">{formError}</p>
        )}
        <Field>
          <Button type="submit" isLoading={isLoading}>
            Login
          </Button>
        </Field>
        <FieldSeparator>Or continue with</FieldSeparator>
        <Field>
          <SocialAuthButton
            text="Login with Google"
            provider="google"
            enabled={!isLoading}
            className="w-full border justify-center gap-2 bg-white hover:bg-gray-50"
          />
          {/* <SocialAuthButton
            text="Login with Facebook"
            provider="facebook"
            enabled={!isLoading}
            className="w-full border justify-center gap-2 bg-white hover:bg-gray-50"
          /> */}
          <SocialAuthButton
            text="Login with LinkedIn"
            provider="linkedin"
            enabled={!isLoading}
            className="w-full border justify-center gap-2 bg-white hover:bg-gray-50"
          />
          <FieldDescription className="text-center">
            Don&apos;t have an account?{' '}
            <Link
              href="/auth/register"
              className="underline underline-offset-4"
            >
              Sign up
            </Link>
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
};
